import React, { useState } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import Tooltip from '@mui/material/Tooltip';

import { sharedClasses } from './sharedClasses';
import { BODY_TEXT, DIVIDER } from '../../../ThemeContext/constants';
import { theme } from '../../../ThemeContext/ThemeObject';

const populateValue = (options: { name: string; id: number | string }[]) => {
  const value = {} as Record<string, boolean>;
  options.forEach((option) => {
    value[option.id] = false;
  });
  return value;
};

const FormCheckboxGroup = ({
  options,
  defaultValues,
  onChange = () => {},
  groupLabel,
  directionRow,
  innerRef,
  error,
  color,
  required,
  disabled
}: {
  options: { name: string; id: number | string; tip: string; label?: React.ReactNode }[];
  onChange?: (value: Record<string, boolean>) => void;
  directionRow?: boolean;
  groupLabel?: string;
  defaultValues?: Record<string | number, boolean>;
  innerRef?: React.Ref<HTMLDivElement>;
  error?: string;
  color?: string;
  required?: boolean;
  disabled?: boolean;
}) => {
  const [value, setValue] = useState(defaultValues ?? populateValue(options));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, id: number | string) => {
    const nextValue = {
      ...value,
      [id]: event.target.checked
    };

    setValue(nextValue);
    onChange(nextValue);
  };

  const checkboxStyle = {
    color: DIVIDER,
    '&.Mui-checked': {
      color: theme.palette.action.main
    },
    '& .MuiSvgIcon-root': {
      fontSize: '22px'
    },
    paddingLeft: 'unset',
    paddingRight: 1
  };

  const labelStyle = {
    '& .MuiTypography-root': {
      color: color ?? BODY_TEXT,
      fontSize: '16px',
      marginLeft: '2px',
      fontFamily: 'Source Sans Pro, sans-serif'
    },
    paddingRight: 2
  };

  return (
    <FormControl>
      {groupLabel ? (
        <FormLabel
          id={groupLabel?.toLowerCase().split(' ').join('-')}
          sx={{ ...sharedClasses.inputLabel, fontSize: '16px' }}
        >
          {groupLabel}
          {required ? <span>*</span> : null}
        </FormLabel>
      ) : null}
      <FormGroup
        ref={innerRef}
        aria-labelledby={groupLabel?.toLowerCase().split(' ').join('-')}
        sx={{ padding: '8px 0px 10px 12px', flexDirection: directionRow ? 'row' : 'column' }}
      >
        {options.map((option) => (
          <Box key={option.id} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              label={option.label ?? option.name}
              sx={labelStyle}
              control={
                <Checkbox
                  disableRipple
                  checked={Boolean(value[option.id])}
                  disabled={disabled}
                  name={option.name}
                  onChange={(event) => handleChange(event, option.id)}
                  sx={checkboxStyle}
                />
              }
            />
            {option.tip ? (
              <Tooltip title={option.tip}>
                <InfoIcon sx={sharedClasses.infoIcon} />
              </Tooltip>
            ) : null}
          </Box>
        ))}
        {error ? <Box sx={sharedClasses.errorBoxCheckbox}>{error}</Box> : null}
      </FormGroup>
    </FormControl>
  );
};

export default FormCheckboxGroup;
